// Base query compartida para todas las API slices RTK Query.
// Apunta al proxy de Supabase e inyecta el token de Auth0 en cada petición
// cuando está disponible (leído del holder de módulo, no de React state).
//
// Con PROXY_REQUIRE_AUTH=false (estado actual) la lógica de reautenticación es
// un no-op: el proxy acepta peticiones sin token, por lo que ninguna devuelve 401
// y el bloque de reintento nunca se ejecuta. Es seguro desplegar ya.
//
// Cuando se active PROXY_REQUIRE_AUTH=true, las peticiones que salgan durante
// la ventana de cold-start (antes de que setAuthToken se resuelva) recibirán un
// 401. El wrapper detecta ese caso, espera hasta que el token esté disponible
// (usando la promesa tokenReadyPromise de authToken.js) y reintenta una vez.

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAuthToken, tokenReadyPromise } from './authToken';

// Tiempo máximo de espera al token antes de devolver el error original (ms)
const TOKEN_WAIT_TIMEOUT_MS = 3000;

const makeRawBaseQuery = (baseUrl) => fetchBaseQuery({
  baseUrl,
  prepareHeaders: async (headers) => {
    let token = getAuthToken();
    // Cold start: si el token aún no está listo, esperamos brevemente a que
    // TokenProvider lo establezca, en lugar de enviar la petición sin token (lo
    // que provocaría un 401 + reintento). Elimina el 401 transitorio del primer
    // render con PROXY_REQUIRE_AUTH=true. Si el token tarda más que el timeout,
    // se envía sin él y el wrapper de reintento sigue actuando como red de seguridad.
    if (!token) {
      await Promise.race([
        tokenReadyPromise,
        new Promise((resolve) => setTimeout(resolve, TOKEN_WAIT_TIMEOUT_MS)),
      ]);
      token = getAuthToken();
    }
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// La petición se envió sin token si el Authorization header no estaba presente
// en el momento de la llamada. Lo detectamos comprobando si getAuthToken()
// devuelve null antes del reintento (estado que ya registramos en el cierre).
// Factory: misma inyección de token + reintento cold-start para cualquier
// endpoint del backend propio (proxy PostgREST, motion-storage, ...).
export const makeAuthedBaseQuery = (baseUrl) => {
  const rawBaseQuery = makeRawBaseQuery(baseUrl);
  return async (args, api, extraOptions) => {
  const tokenAtSendTime = getAuthToken();
  const result = await rawBaseQuery(args, api, extraOptions);

  // Reintento sólo si:
  //   1. La respuesta fue un 401
  //   2. No había token cuando se envió la petición (cold-start race)
  // Si ya había token y aun así hubo 401, es un problema de autorización real —
  // no reintentamos para evitar bucles.
  if (result.error?.status === 401 && !tokenAtSendTime) {
    try {
      // Esperar a que el token esté disponible, con timeout de seguridad
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), TOKEN_WAIT_TIMEOUT_MS)
      );
      await Promise.race([tokenReadyPromise, timeoutPromise]);
    } catch {
      // Timeout alcanzado — devolver el error original sin reintentar
      return result;
    }

    // Reintentar la petición una sola vez (el token ya está en el holder)
    return rawBaseQuery(args, api, extraOptions);
  }

  return result;
  };
};

export const baseQuery = makeAuthedBaseQuery('/api/supabase-proxy');
